import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { handleAuthError } from '../services/api/authUtils';
import { useWorkspace } from '../contexts/WorkspaceContext';
import {
  Page,
  Layout,
  Card,
  BlockStack,
  Text,
  Button,
  InlineStack,
  Badge,
  Spinner
} from '@shopify/polaris';
import { RefreshIcon } from '@shopify/polaris-icons';
import { adminApi } from '../services/api/adminApi';
import './WorkspacePage.css';

export const WorkspacePage = () => {
  const { user, logout } = useAuth();
  const { workspaces, isLoading, error, refreshWorkspaces, selectWorkspace } = useWorkspace();
  const navigate = useNavigate();
  const [workspaceAnalytics, setWorkspaceAnalytics] = useState({});
  const [loadingAnalytics, setLoadingAnalytics] = useState(false);

  const handleWorkspaceSelect = async (workspace) => {
    try {
      const result = await selectWorkspace(workspace);
      if (result.success) {
        navigate('/app', { state: { workspace, initialTabId: 'configurations' } });
      } else {
        // If selection failed due to auth, redirect to login
        handleAuthError({ response: { status: 401 } });
      }
    } catch (error) {
      // Treat unknown errors as possible session expiry
      handleAuthError({ response: { status: 401 } });
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // Fetch analytics for all workspaces
  const fetchWorkspaceAnalytics = async () => {
    if (!workspaces || workspaces.length === 0) return;
    
    setLoadingAnalytics(true);
    const analyticsMap = {};
    
    try {
      // Fetch analytics for each workspace in parallel
      const analyticsPromises = workspaces.map(async (workspace) => {
        if (!workspace.id) return;
        
        try {
          const response = await adminApi.getAdminAnalytics('7d', { tenantId: workspace.id });
          if (response.success) {
            analyticsMap[workspace.id] = response;
          }
        } catch (err) {
          // Gracefully handle errors (e.g., user doesn't have admin permissions)
        }
      });
      
      await Promise.all(analyticsPromises);
      setWorkspaceAnalytics(analyticsMap);
    } catch (error) {
    } finally {
      setLoadingAnalytics(false);
    }
  };

  useEffect(() => {
    if (workspaces && workspaces.length > 0) {
      fetchWorkspaceAnalytics();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaces]);

  return (
    <Page
      title="Select Workspace"
      primaryAction={{
        content: 'Logout',
        onAction: handleLogout,
        variant: 'tertiary'
      }}
    >
      <Layout>
        <Layout.Section>
          <Card className="welcome-card">
            <BlockStack gap="loose">
              <div className="welcome-header">
                <Text variant="headingLg" fontWeight="bold" className="welcome-title">
                  Welcome, {user?.name}! 👋
                </Text>
                <Text variant="bodyMd" color="subdued" className="welcome-subtitle">
                  Please select a workspace to continue to your dashboard
                </Text>
              </div>

              <div className="user-info-grid">
                <div className="user-info-item">
                  <Text variant="bodySm" fontWeight="bold" className="user-info-label">
                    Email
                  </Text>
                  <div className="user-info-value">
                    {user?.email}
                  </div>
                </div>

                <div className="user-info-item">
                  <Text variant="bodySm" fontWeight="bold" className="user-info-label">
                    Status
                  </Text>
                  <div className="user-info-value">
                    <Badge status="success" className="user-status-badge">Active</Badge>
                  </div>
                </div>
              </div>
            </BlockStack>
          </Card>
        </Layout.Section>

        <Layout.Section>
          <BlockStack gap="loose">
            <div className="section-header">
              <InlineStack align="space-between">
                <div>
                  <Text variant="headingMd" fontWeight="bold" className="section-title">
                    Available Workspaces
                  </Text>
                  <Text variant="bodySm" color="subdued" className="section-subtitle">
                    Choose your environment to get started
                  </Text>
                </div>
                <Button
                  icon={RefreshIcon}
                  variant="tertiary"
                  onClick={refreshWorkspaces}
                  disabled={isLoading}
                  className="refresh-button"
                >
                  Refresh
                </Button>
              </InlineStack>
            </div>

            {error && (
              <div className="error-banner">
                <Text variant="bodyMd" color="critical">
                  Error loading workspaces: {error}
                </Text>
              </div>
            )}

            {isLoading ? (
              <div className="loading-container">
                <Spinner size="large" />
                <Text variant="bodyMd" color="subdued" className="loading-text">
                  Loading workspaces...
                </Text>
              </div>
            ) : workspaces.length === 0 ? (
              <div className="empty-state">
                <Text variant="bodyMd" color="subdued">
                  No workspaces available. Please contact your administrator.
                </Text>
              </div>
            ) : (
              <div className="workspace-grid">
                {workspaces.map((workspace) => (
                  <div
                    key={workspace.id}
                    className="workspace-card clickable"
                    role="button"
                    tabIndex={0}
                    onClick={() => handleWorkspaceSelect(workspace)}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleWorkspaceSelect(workspace); }}
                  >
                    <Card sectioned className="workspace-card-inner">
                      <BlockStack gap="medium">
                        <div className="workspace-header">
                          <InlineStack align="space-between">
                            <Text variant="headingMd" fontWeight="bold" className="workspace-name">
                              {workspace.name}
                            </Text>
                            <Badge tone={workspace.status === 'active' ? 'success' : 'warning'} className="workspace-status">
                              {workspace.status}
                            </Badge>
                          </InlineStack>
                        </div>

                        {workspace.id && (
                          <Text variant="bodySm" color="subdued">
                            Tenant ID: {workspace.id}
                          </Text>
                        )}

                        <Text variant="bodyMd" color="subdued" className="workspace-description">
                          {workspace.description}
                        </Text>

                        <div className="integration-panel">
                          {workspace.integrations ? (
                            <>
                              {workspace.integrations.backend?.provider && (
                                <div className="integration-pill backend" title="Backend">
                                  {workspace.integrations.backend.provider}
                                </div>
                              )}
                              {(workspace.integrations.backend?.provider || workspace.integrations.frontend?.provider) && (
                                <div className="integration-arrow">⇄</div>
                              )}
                              {workspace.integrations.frontend?.provider && (
                                <div className="integration-pill frontend" title="Frontend">
                                  {workspace.integrations.frontend.provider}
                                </div>
                              )}
                            </>
                          ) : null}
                        </div>

                        {workspaceAnalytics[workspace.id] && (
                          <div className="analytics-summary">
                            <Text variant="bodySm" fontWeight="semibold" color="subdued" style={{ marginBottom: '8px' }}>
                              Analytics (Last 7 Days)
                            </Text>
                            <div className="analytics-grid">
                              <div className="analytics-item">
                                <Text variant="bodySm" color="subdued">Syncs</Text>
                                <Text variant="bodyMd" fontWeight="bold">
                                  {workspaceAnalytics[workspace.id].summary?.totalSyncs || 0}
                                </Text>
                              </div>
                              <div className="analytics-item">
                                <Text variant="bodySm" color="subdued">Success</Text>
                                <Text variant="bodyMd" fontWeight="bold">
                                  {Math.round(workspaceAnalytics[workspace.id].summary?.successRate || 0)}%
                                </Text>
                              </div>
                              <div className="analytics-item">
                                <Text variant="bodySm" color="subdued">Avg Time</Text>
                                <Text variant="bodyMd" fontWeight="bold">
                                  {Math.round(workspaceAnalytics[workspace.id].summary?.avgDuration || 0)}ms
                                </Text>
                              </div>
                              <div className="analytics-item">
                                <Text variant="bodySm" color="subdued">Records</Text>
                                <Text variant="bodyMd" fontWeight="bold">
                                  {workspaceAnalytics[workspace.id].summary?.totalRecords || 0}
                                </Text>
                              </div>
                            </div>
                          </div>
                        )}

                        <div className="workspace-actions">
                          <Button
                            fullWidth
                            onClick={(e) => { e.stopPropagation(); handleWorkspaceSelect(workspace); }}
                            disabled={workspace.status !== 'active'}
                            className="select-workspace-button"
                          >
                            {`Select ${workspace.name}`}
                          </Button>
                        </div>
                      </BlockStack>
                    </Card>
                  </div>
                ))}
              </div>
            )}
          </BlockStack>
        </Layout.Section>
      </Layout>
    </Page>
  );
};
